import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, Users, TrendingUp, Handshake, MessageSquare, Code, Search, Database, ChevronRight, Award, Trophy, Zap, Sparkles, Brain, BarChart3, BookOpen, Lightbulb, AlertTriangle, CheckCircle, ArrowRight, Clock, Shield } from 'lucide-react';
import { directeurCompteCategories, directeurCompteLevels, type DirecteurCompteCategory } from '../data/directeurCompteReferentiel';
import DirecteurCompteSpiderChart from './DirecteurCompteSpiderChart';
import DirecteurCompteRACITable from './DirecteurCompteRACITable';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface DirecteurCompteReferentielModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const iconMap: Record<string, any> = {
  Target, Users, TrendingUp, Handshake, MessageSquare, Code, Search, Database
};

export default function DirecteurCompteReferentielModal({ isOpen, onClose }: DirecteurCompteReferentielModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<DirecteurCompteCategory | null>(null);
  const [selectedSubCompetence, setSelectedSubCompetence] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'contenu' | 'matrice'>('contenu');
  const [subActiveTab, setSubActiveTab] = useState<'contenu' | 'matrice'>('contenu');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['referentiel_directeur_compte_last_ai_analysis'] ?? null) : null;

  const getCurrentLevel = (score: number) => {
    if (score >= 90) return directeurCompteLevels[2];
    if (score >= 75) return directeurCompteLevels[1];
    if (score >= 60) return directeurCompteLevels[0];
    return null;
  };

  const globalScore = Math.round(
    directeurCompteCategories.reduce((sum, cat) => sum + cat.currentScore, 0) / directeurCompteCategories.length
  );
  const currentLevel = getCurrentLevel(globalScore);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-[98vw] w-full max-h-[98vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-amber-600 text-white p-6 rounded-t-xl z-10">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-4">
                    {/* Avatar d'Antoine Giroux */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full ring-4 ring-white/30 overflow-hidden shadow-lg">
                        <img 
                          src="/AG-transparent.png" 
                          alt="Antoine Giroux"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h2 className="text-3xl font-bold">Référentiel Directeur de Compte - Standards d'Excellence</h2>
                        {/* Badge AI - Disponible dans AI Assistant */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                          className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                          title="Disponible dans AI Assistant"
                        >
                          <Brain className="w-4 h-4 text-purple-200" />
                          <span className="text-xs text-purple-100 font-semibold">AI</span>
                        </motion.div>
                      </div>
                      <p className="text-white/90">Cadre de compétences BZ pour les Directeurs de Compte (B2B Sales)</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-white/95 text-sm font-medium">Leader:</span>
                        <span className="text-white font-semibold text-sm">Antoine Giroux</span>
                        <span className="text-white/95 text-sm font-medium">- Directeur des ventes</span>
                      </div>
                      {lastAnalysisDate && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-white/80">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Score Global & Niveau */}
                <div className="mt-6 flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <Award className="w-12 h-12 text-yellow-300" />
                    <div>
                      <div className="text-sm text-white/70">Score Global</div>
                      <div className="text-3xl font-bold">{globalScore}%</div>
                    </div>
                  </div>
                  {currentLevel && (
                    <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white/70">Niveau Actuel</div>
                          <div className="text-xl font-bold" style={{ color: currentLevel.color }}>
                            {currentLevel.level}
                          </div>
                          <div className="text-xs text-white/60">{currentLevel.description}</div>
                        </div>
                        <div className="flex gap-2">
                          {directeurCompteLevels.map((level) => (
                            <div
                              key={level.level}
                              className={`w-3 h-3 rounded-full ${
                                globalScore >= level.minScore ? 'opacity-100' : 'opacity-30'
                              }`}
                              style={{ backgroundColor: level.color }}
                              title={`${level.level}: ${level.minScore}%+`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Niveaux de Progression */}
              <div className="p-6 bg-gray-50 border-b">
                <h3 className="text-lg font-semibold text-bz-dark mb-4">Niveaux de Progression Directeur de Compte</h3>
                <div className="grid grid-cols-3 gap-4">
                  {directeurCompteLevels.map((level) => (
                    <div
                      key={level.level}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        globalScore >= level.minScore
                          ? 'bg-white border-green-500 shadow-md'
                          : 'bg-white/50 border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: level.color }}
                        />
                        <h4 className="font-bold text-gray-800">{level.level}</h4>
                      </div>
                      <div className="text-2xl font-bold mb-1" style={{ color: level.color }}>
                        {level.minScore}%+
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{level.description}</p>
                      
                      {/* Certification PRIsME Requise */}
                      {level.prismeCertification && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Trophy className="w-4 h-4 text-purple-600" />
                            <p className="text-xs font-bold text-purple-600">{level.prismeCertification}</p>
                          </div>
                          <p className="text-xs text-gray-500 leading-tight">{level.prismeDescription}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 🏆 SECTION CERTIFICATION PRIsME - LE MÉTA-SKILL */}
              <div className="p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border-b-4 border-purple-500">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                      🏆 Certification PRIsME - Le Méta-Skill
                    </h3>
                    <p className="text-gray-600 font-medium">Le catalyseur qui TRANSFORME toutes tes compétences commerciales</p>
                  </div>
                </div>

                {/* Les 3 Visions Combinées */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {/* Vision 1: Mathématique */}
                  <div className="bg-white rounded-lg p-4 border-2 border-purple-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <h4 className="font-bold text-purple-900">Effet Multiplicateur</h4>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <strong className="text-purple-600">PRIsME Certifié</strong> = Performance de base<br/>
                      <strong className="text-purple-600">PRIsME Expert</strong> = +15% toutes compétences<br/>
                      <strong className="text-purple-600">PRIsME Maître</strong> = Multiplicateur 1.5x
                    </p>
                  </div>

                  {/* Vision 2: Directe (Style Dany) */}
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-4 border-2 border-orange-300 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-orange-600" />
                      <h4 className="font-bold text-orange-900">La Vérité</h4>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      PRIsME n'est <strong className="text-orange-600">PAS</strong> un skill parmi d'autres. C'est le <strong className="text-orange-600">CATALYSEUR</strong> qui transforme un bon vendeur en <strong className="text-orange-600">EXCELLENT</strong> Directeur de Compte. Sans PRIsME, tu plafonnes. Avec PRIsME, tu décoles.
                    </p>
                  </div>

                  {/* Vision 3: Métaphore */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border-2 border-blue-300 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <h4 className="font-bold text-blue-900">L'Orchestrateur</h4>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Si le Référentiel Directeur de Compte est l'<strong className="text-blue-600">orchestre</strong>, la Certification PRIsME est le <strong className="text-blue-600">chef d'orchestre</strong>. Elle coordonne, amplifie et harmonise toutes tes compétences commerciales.
                    </p>
                  </div>
                </div>

                {/* Exigences Concrètes */}
                <div className="bg-white rounded-lg p-6 border-2 border-purple-300 shadow-md">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    Exigences & Impact Carrière
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-purple-900 mb-2">📋 Exigences:</p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• <strong>Embauche:</strong> Engagement PRIsME Certifié dans 6 mois</li>
                        <li>• <strong>Évaluation annuelle:</strong> Certification = 25% du score</li>
                        <li>• <strong>Promotion:</strong> Impossible sans niveau PRIsME requis</li>
                        <li>• <strong>Bonus:</strong> Débloqués par niveau de certification</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-purple-900 mb-2">💰 Impact Salarial:</p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• <strong className="text-amber-600">PRIsME Certifié:</strong> +1% salaire de base</li>
                        <li>• <strong className="text-gray-600">PRIsME Expert:</strong> +3% salaire de base</li>
                        <li>• <strong className="text-yellow-600">PRIsME Maître:</strong> +5% salaire de base</li>
                        <li>• <strong className="text-purple-600">PRIsME Légende:</strong> Package leadership + equity</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Message Final Puissant */}
                <div className="mt-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 rounded-lg p-6 text-white shadow-xl">
                  <p className="text-center text-lg font-bold mb-2">
                    💎 Maîtriser PRIsME = Maîtriser l'Excellence Commerciale
                  </p>
                  <p className="text-center text-white/90">
                    Tu ne maîtrises pas juste un outil. Tu maîtrises le <strong>MINDSET d'excellence</strong>, l'<strong>IA</strong>, la <strong>stratégie commerciale</strong>, l'<strong>exécution</strong>, la <strong>communication</strong>, et le <strong>leadership</strong>. Quand tu es PRIsME Maître, tu n'es pas juste bon dans PRIsME. <strong className="text-yellow-300">Tu es EXCELLENT PARTOUT.</strong>
                  </p>
                </div>
              </div>

              {/* 🤝 MATCH CLIENT–DIRECTEUR DE COMPTE */}
              <div className="p-6 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-b">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl shadow-lg">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-700 to-emerald-700">
                      🤝 Match Client–Directeur de Compte
                    </h3>
                    <p className="text-gray-700 font-medium">Orienté EMPLOYÉ (évolution Directeur de Compte) mais utilisé comme vitrine CLIENT pour garantir le fit parfait.</p>
                  </div>
                </div>

                {/* Grille de mapping */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                    <p className="text-xs text-gray-500 mb-1">Profil Client</p>
                    <p className="font-bold text-gray-900 mb-2">PME en structuration (budget limité, besoins basiques)</p>
                    <p className="text-sm text-gray-700 mb-2">Besoins: première approche structurée, proposition claire, accompagnement rassurant.</p>
                    <div className="text-sm">
                      <p className="font-semibold text-green-700">Recommandé: Junior Directeur de Compte → Directeur de Compte</p>
                      <p className="text-gray-600">Accompagné par un Senior ponctuellement pour sécuriser.</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                    <p className="text-xs text-gray-500 mb-1">Profil Client</p>
                    <p className="font-bold text-gray-900 mb-2">ETI en croissance (roadmap chargé, budget moyen)</p>
                    <p className="text-sm text-gray-700 mb-2">Besoins: expertise commerciale, solutions adaptées, ROI démontré.</p>
                    <div className="text-sm">
                      <p className="font-semibold text-green-700">Recommandé: Directeur de Compte → Senior Directeur de Compte</p>
                      <p className="text-gray-600">Certification PRIsME Expert requise; coaching stratégique.</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                    <p className="text-xs text-gray-500 mb-1">Profil Client</p>
                    <p className="font-bold text-gray-900 mb-2">Grande entreprise (exigence haute, budget élevé)</p>
                    <p className="text-sm text-gray-700 mb-2">Besoins: vision stratégique, solutions complexes, relation C-Level.</p>
                    <div className="text-sm">
                      <p className="font-semibold text-green-700">Recommandé: Senior Directeur de Compte (PRIsME Maître)</p>
                      <p className="text-gray-600">Peut parrainer 1-2 Directeurs de Compte pour effet de levier.</p>
                    </div>
                  </div>
                </div>

                {/* Critères d'assignation rapides */}
                <div className="mt-6 bg-white rounded-lg p-4 border-2 border-green-200">
                  <p className="font-semibold text-gray-900 mb-2">🎯 Critères d'assignation</p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm text-gray-700">
                    <div>
                      <p className="font-medium text-green-700">Complexité Client</p>
                      <p>Simple → Junior encadré • Moyenne → Directeur de Compte • Complexe → Senior</p>
                    </div>
                    <div>
                      <p className="font-medium text-green-700">Valeur Contrat</p>
                      <p>Plus la valeur est élevée, plus le niveau requis augmente</p>
                    </div>
                    <div>
                      <p className="font-medium text-green-700">Relation & Confiance</p>
                      <p>Clients stratégiques, C-Level, gouvernance complexe</p>
                    </div>
                  </div>
                </div>

                {/* Message d'explication */}
                <div className="mt-4 bg-green-600/10 border border-green-600/30 rounded-lg p-4">
                  <p className="text-sm text-gray-800">
                    Ce référentiel est d'abord un <strong>outil d'évolution employé</strong> (coaching, progression, certification).
                    Il devient aussi une <strong>vitrine client</strong> pour justifier l'assignation du bon Directeur de Compte et garantir le <strong>match parfait</strong>.
                  </p>
                </div>
              </div>

              {/* Alignement avec les Standards Internationaux */}
              <div className="p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-b">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700">
                      🛡️ Alignement avec les Standards Internationaux
                    </h3>
                    <p className="text-gray-700 font-medium">Ce référentiel s'appuie sur les meilleures pratiques reconnues mondialement</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* ITIL 4 */}
                  <div className="bg-white rounded-lg p-5 border-2 border-blue-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Shield className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-bold text-gray-800">ITIL 4 - Service Management</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Le référentiel intègre les principes ITIL 4 pour la gestion de la relation client et la livraison de services :
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Gestion de la relation client</strong> : Approche structurée de la relation commerciale</li>
                      <li>• <strong>Value streams</strong> : Optimisation des processus de vente</li>
                      <li>• <strong>Guiding principles</strong> : Focus sur la valeur, collaboration, amélioration continue</li>
                      <li>• <strong>Service level management</strong> : Gestion des attentes et SLA commerciaux</li>
                    </ul>
                  </div>

                  {/* COBIT 2019 */}
                  <div className="bg-white rounded-lg p-5 border-2 border-indigo-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Shield className="w-5 h-5 text-indigo-600" />
                      </div>
                      <h4 className="font-bold text-gray-800">COBIT 2019 - Governance</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Alignement avec COBIT 2019 pour la gouvernance commerciale et la gestion des risques :
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>EDM (Evaluate, Direct, Monitor)</strong> : Cadre de gouvernance commerciale</li>
                      <li>• <strong>Risk management</strong> : Identification et gestion des risques commerciaux</li>
                      <li>• <strong>Performance management</strong> : KPIs et métriques alignées avec les objectifs</li>
                      <li>• <strong>Information & data</strong> : Gestion de l'information client et commerciale</li>
                    </ul>
                  </div>

                  {/* PMBOK / Agile */}
                  <div className="bg-white rounded-lg p-5 border-2 border-purple-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Shield className="w-5 h-5 text-purple-600" />
                      </div>
                      <h4 className="font-bold text-gray-800">PMBOK / Agile - Project Management</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Intégration des meilleures pratiques de gestion de projet pour les cycles commerciaux :
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Stakeholder management</strong> : Gestion des parties prenantes commerciales</li>
                      <li>• <strong>Risk management</strong> : Identification proactive des risques de vente</li>
                      <li>• <strong>Agile methodology</strong> : Adaptation rapide aux changements du marché</li>
                      <li>• <strong>Communication management</strong> : Communication efficace avec les clients</li>
                    </ul>
                  </div>

                  {/* ISO 9001 / Excellence Opérationnelle */}
                  <div className="bg-white rounded-lg p-5 border-2 border-green-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Award className="w-5 h-5 text-green-600" />
                      </div>
                      <h4 className="font-bold text-gray-800">ISO 9001 - Excellence Opérationnelle</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Principes d'amélioration continue et de qualité appliqués à l'excellence commerciale :
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Process approach</strong> : Processus commerciaux standardisés et optimisés</li>
                      <li>• <strong>Continuous improvement</strong> : Amélioration continue des performances</li>
                      <li>• <strong>Customer focus</strong> : Orientation client au cœur de toutes les actions</li>
                      <li>• <strong>Evidence-based decisions</strong> : Décisions basées sur les données et KPIs</li>
                    </ul>
                  </div>
                </div>

                {/* Message de synthèse */}
                <div className="mt-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-lg p-6 text-white shadow-xl">
                  <p className="text-center text-lg font-bold mb-2">
                    🌟 Un Référentiel Normé et Professionnel
                  </p>
                  <p className="text-center text-white/90">
                    Ce référentiel ne réinvente pas la roue - il s'appuie sur les <strong>standards reconnus mondialement</strong> (ITIL 4, COBIT 2019, PMBOK, ISO 9001) pour garantir une approche <strong>professionnelle, structurée et alignée</strong> avec les meilleures pratiques de l'industrie. Chaque compétence est évaluée selon des critères <strong>mesurables et objectifs</strong>, permettant une progression claire et transparente vers l'excellence commerciale.
                  </p>
                </div>
              </div>

              {/* Catégories Principales */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-bz-dark mb-4">
                  8 Catégories de Compétences
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {directeurCompteCategories.map((category, index) => {
                    const Icon = iconMap[category.icon] || Target;
                    const meetsMinimum = category.currentScore >= category.minScore;
                    
                    return (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => setSelectedCategory(category)}
                        className="bg-white border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-orange-500 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: `${category.color}20` }}
                          >
                            <Icon className="w-6 h-6" style={{ color: category.color }} />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-bold text-gray-800 group-hover:text-orange-600 transition-colors">
                                {category.name}
                              </h4>
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                            
                            {/* Progress Bar */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Minimum: {category.minScore}%</span>
                                <span
                                  className="font-bold"
                                  style={{ color: meetsMinimum ? category.color : '#E74C3C' }}
                                >
                                  Score: {category.currentScore}%
                                </span>
                              </div>
                              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div className="relative h-full">
                                  {/* Barre de minimum */}
                                  <div
                                    className="absolute h-full bg-gray-400/30"
                                    style={{ width: `${category.minScore}%` }}
                                  />
                                  {/* Barre de score actuel */}
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${category.currentScore}%` }}
                                    transition={{ duration: 1, delay: index * 0.05 }}
                                    className="absolute h-full rounded-full"
                                    style={{ backgroundColor: category.color }}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            {/* Indicateur */}
                            <div className="mt-2 text-xs">
                              {meetsMinimum ? (
                                <span className="text-green-600 font-medium">✓ Atteint</span>
                              ) : (
                                <span className="text-red-600 font-medium">
                                  ⚠ {category.minScore - category.currentScore}% à gagner
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Graphique Spider Réel */}
              <div className="p-6 bg-gradient-to-br from-gray-50 to-orange-50 border-t">
                <DirecteurCompteSpiderChart />
              </div>

              {/* Tableau RACI */}
              <div className="p-6 border-t">
                <DirecteurCompteRACITable />
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-between items-center border-t">
                <div className="text-sm text-gray-600">
                  <strong>Note:</strong> Ce référentiel sert à évaluer et développer les compétences des Directeurs de Compte BZ
                </div>
                <button
                  onClick={onClose}
                  className="aikup-button"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>

          {/* Modal de Détails des Sous-Compétences */}
          <AnimatePresence>
            {selectedCategory && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                onClick={() => setSelectedCategory(null)}
              >
                <div className="absolute inset-0 bg-black/60" />
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col"
                >
                  <div
                    className="p-6 text-white rounded-t-xl"
                    style={{ backgroundColor: selectedCategory.color }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-2xl font-bold mb-2">{selectedCategory.name}</h3>
                        <p className="text-white/90">{selectedCategory.description}</p>
                      </div>
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className="text-white hover:bg-white/10 rounded-full p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Onglets pour la catégorie */}
                  <div className="border-b border-gray-200 bg-gray-50">
                    <div className="flex overflow-x-auto">
                          {[
                            { id: 'contenu', label: 'Contenu Complet', icon: BookOpen },
                            { id: 'matrice', label: 'Matrice de Progression', icon: BarChart3 },
                          ].map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-all whitespace-nowrap ${
                              activeTab === tab.id
                                ? 'border-b-2 text-white'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                            style={
                              activeTab === tab.id
                                ? {
                                    borderBottomColor: selectedCategory.color,
                                    backgroundColor: `${selectedCategory.color}15`,
                                  }
                                : {}
                            }
                          >
                            <Icon className="w-4 h-4" />
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-8 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
                    {/* Contenu Complet - Fusion de tous les onglets */}
                    {activeTab === 'contenu' && (
                      <div className="space-y-8">
                        {/* Section Pourquoi */}
                        <div className="bg-gradient-to-br from-white via-blue-50/30 to-white p-8 rounded-2xl border-2 border-blue-100 shadow-sm">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="p-4 rounded-xl" style={{ backgroundColor: `${selectedCategory.color}15` }}>
                              <Target className="w-8 h-8" style={{ color: selectedCategory.color }} />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-2xl font-bold text-gray-800 mb-3">Pourquoi cette compétence est cruciale</h4>
                              <p className="text-gray-700 leading-relaxed text-lg">{selectedCategory.pourquoiImportant || selectedCategory.description}</p>
                            </div>
                          </div>
                        </div>

                        {/* Esprit */}
                        {selectedCategory.esprit && (
                          <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 p-8 rounded-2xl border-2 border-yellow-300 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="p-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg">
                                <Lightbulb className="w-8 h-8 text-white" />
                              </div>
                              <div>
                                <h4 className="text-2xl font-bold text-gray-800">L'Esprit de cette compétence</h4>
                                <p className="text-sm text-gray-600 mt-1">La mentalité et l'approche à adopter</p>
                              </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-yellow-200">
                              <p className="text-gray-800 leading-relaxed text-lg font-medium">{selectedCategory.esprit}</p>
                            </div>
                          </div>
                        )}

                        {/* Psychologie */}
                        {selectedCategory.psychologie && (
                          <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-fuchsia-50 p-8 rounded-2xl border-2 border-purple-300 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
                                <Brain className="w-8 h-8 text-white" />
                              </div>
                              <div>
                                <h4 className="text-2xl font-bold text-gray-800">La Psychologie derrière</h4>
                                <p className="text-sm text-gray-600 mt-1">Pièges à éviter et insights psychologiques</p>
                              </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-purple-200">
                              <div className="prose prose-lg max-w-none">
                                <p className="text-gray-800 leading-relaxed whitespace-pre-line text-base font-medium">{selectedCategory.psychologie}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pédagogie */}
                        {selectedCategory.explicationPedagogique && (
                          <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 rounded-2xl border-2 border-green-300 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                                <BookOpen className="w-8 h-8 text-white" />
                              </div>
                              <div>
                                <h4 className="text-2xl font-bold text-gray-800">Explication Pédagogique</h4>
                                <p className="text-sm text-gray-600 mt-1">Comprendre en profondeur cette compétence</p>
                              </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-green-200">
                              <div className="prose prose-lg max-w-none">
                                <p className="text-gray-800 leading-relaxed text-base font-medium">{selectedCategory.explicationPedagogique}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cas d'usage */}
                        {selectedCategory.casUsage && selectedCategory.casUsage.length > 0 && (
                          <div className="bg-gradient-to-br from-orange-50/50 via-white to-orange-50/30 p-8 rounded-2xl border-2 border-orange-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                              <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                                <Zap className="w-6 h-6 text-white" />
                              </div>
                              <h4 className="text-2xl font-bold text-gray-800">Cas d'usage concrets</h4>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              {selectedCategory.casUsage.map((cas, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: idx * 0.1 }}
                                  className="bg-white p-5 rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-md transition-all group"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                                      <span className="text-orange-600 font-bold text-sm">{idx + 1}</span>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed flex-1">{cas}</p>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <h4 className="text-lg font-bold text-gray-800 mb-4">
                            Sous-Compétences ({selectedCategory.subCompetences.length})
                          </h4>
                          
                          <div className="space-y-4">
                            {selectedCategory.subCompetences.map((sub, idx) => (
                              <motion.div
                                key={sub.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                onClick={() => setSelectedSubCompetence(sub.id)}
                                className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 hover:border-orange-400 cursor-pointer transition-all hover:shadow-md group"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-bold text-gray-800 group-hover:text-orange-600 transition-colors">
                                    {sub.name}
                                  </h5>
                                  <div className="text-right flex items-center gap-2">
                                    <div>
                                      <div className="text-xs text-gray-500">Min: {sub.minRequired}%</div>
                                      <div
                                        className="text-lg font-bold"
                                        style={{ color: sub.score >= sub.minRequired ? selectedCategory.color : '#E74C3C' }}
                                      >
                                        {sub.score}%
                                      </div>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
                                  </div>
                                </div>
                                
                                <p className="text-sm text-gray-600 mb-3">{sub.description}</p>
                                
                                <div className="bg-white rounded-full h-2 overflow-hidden">
                                  <div className="relative h-full">
                                    <div
                                      className="absolute h-full bg-gray-300"
                                      style={{ width: `${sub.minRequired}%` }}
                                    />
                                    <div
                                      className="absolute h-full rounded-full"
                                      style={{
                                        width: `${sub.score}%`,
                                        backgroundColor: selectedCategory.color
                                      }}
                                    />
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Matrice de Progression */}
                    {activeTab === 'matrice' && (
                      <div className="space-y-8">
                        <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50 p-8 rounded-2xl border-2 border-blue-300 shadow-lg">
                          <div className="flex items-center gap-4 mb-8">
                            <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-lg">
                              <BarChart3 className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h4 className="text-2xl font-bold text-gray-800">Matrice de Progression</h4>
                              <p className="text-base text-gray-600 mt-1">Identifie ton niveau actuel et les exemples concrets pour progresser</p>
                            </div>
                          </div>
                          
                          {/* Matrice avec 3 niveaux */}
                          <div className="grid md:grid-cols-3 gap-6">
                            {directeurCompteLevels.map((level, idx) => {
                              // Exemples concrets pour cette catégorie selon le niveau
                              const getExamples = () => {
                                if (selectedCategory.id === 'strategie-commerciale') {
                                  if (level.level === 'Junior Directeur de Compte') {
                                    return [
                                      'Plan commercial annuel basique avec objectifs chiffrés',
                                      'Forecast mensuel avec précision ±15%',
                                      'Pipeline équilibré à 2x le quota',
                                      'Révision trimestrielle avec ajustements mineurs'
                                    ];
                                  } else if (level.level === 'Directeur de Compte') {
                                    return [
                                      'Plan commercial stratégique avec analyse de marché',
                                      'Forecast mensuel précis à ±5%',
                                      'Pipeline équilibré à 3x le quota',
                                      'Révision trimestrielle avec stratégies d\'ajustement',
                                      'Stratégie par secteur documentée et exécutée'
                                    ];
                                  } else {
                                    return [
                                      'Plan commercial visionnaire avec scénarios multiples',
                                      'Forecast mensuel précis à ±2%',
                                      'Pipeline équilibré à 4x le quota avec diversification',
                                      'Révision continue avec innovations stratégiques',
                                      'Stratégie par secteur avec ROI démontré',
                                      'Mentorat d\'autres directeurs de compte'
                                    ];
                                  }
                                }
                                // Exemples génériques pour autres catégories
                                return [
                                  `Niveau ${idx + 1}: Exemple concret 1`,
                                  `Niveau ${idx + 1}: Exemple concret 2`,
                                  `Niveau ${idx + 1}: Exemple concret 3`
                                ];
                              };

                              const examples = getExamples();
                              // Déterminer si c'est le niveau actuel basé sur le score de la catégorie
                              const categoryScore = selectedCategory.currentScore;
                              const isCurrentLevel = categoryScore >= level.minScore && 
                                (idx === directeurCompteLevels.length - 1 || categoryScore < directeurCompteLevels[idx + 1]?.minScore);

                                    return (
                                      <motion.div
                                        key={level.level}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                        className={`bg-white rounded-xl p-6 border-2 shadow-md transition-all hover:shadow-xl ${
                                          isCurrentLevel 
                                            ? 'border-orange-400 ring-4 ring-orange-200 scale-105' 
                                            : 'border-gray-200 hover:border-gray-400'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3 mb-5">
                                          <div
                                            className="w-6 h-6 rounded-full shadow-md"
                                            style={{ backgroundColor: level.color }}
                                          />
                                          <div className="flex-1">
                                            <h5 className="font-bold text-gray-800 text-lg">{level.level}</h5>
                                            <p className="text-xs text-gray-500 font-medium">{level.minScore}%+ requis</p>
                                          </div>
                                          {isCurrentLevel && (
                                            <span className="px-3 py-1 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-md animate-pulse">
                                              TON NIVEAU
                                            </span>
                                          )}
                                        </div>
                                        
                                        <div className="space-y-3">
                                          <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                            <Target className="w-4 h-4" style={{ color: level.color }} />
                                            Exemples concrets à atteindre :
                                          </p>
                                          {examples.map((example, exIdx) => (
                                            <div
                                              key={exIdx}
                                              className="flex items-start gap-3 text-sm text-gray-700 bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                                            >
                                              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                              <span className="leading-relaxed">{example}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </motion.div>
                                    );
                            })}
                          </div>

                          {/* Guide d'auto-évaluation */}
                          <div className="mt-8 bg-gradient-to-br from-white to-blue-50/50 rounded-xl p-6 border-2 border-blue-300 shadow-md">
                            <h5 className="font-bold text-gray-800 mb-4 flex items-center gap-3 text-lg">
                              <div className="p-2 bg-blue-500 rounded-lg">
                                <Target className="w-5 h-5 text-white" />
                              </div>
                              Comment t'auto-évaluer
                            </h5>
                            <div className="space-y-2 text-sm text-gray-700">
                              <p className="font-semibold">✅ Coche les exemples que tu maîtrises :</p>
                              <div className="grid md:grid-cols-3 gap-3 mt-3">
                                {directeurCompteLevels.map((level) => {
                                  const examples = level.level === 'Junior Directeur de Compte' 
                                    ? ['Plan commercial annuel basique', 'Forecast mensuel ±15%', 'Pipeline 2x quota']
                                    : level.level === 'Directeur de Compte'
                                    ? ['Plan stratégique avec analyse', 'Forecast ±5%', 'Pipeline 3x quota', 'Stratégie par secteur']
                                    : ['Plan visionnaire multi-scénarios', 'Forecast ±2%', 'Pipeline 4x quota', 'Innovations stratégiques', 'Mentorat'];
                                  
                                  return (
                                    <div key={level.level} className="space-y-1">
                                      <p className="font-medium text-xs text-gray-600">{level.level}:</p>
                                      {examples.slice(0, 3).map((ex, i) => (
                                        <label key={i} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                          <input type="checkbox" className="w-3 h-3" />
                                          <span>{ex}</span>
                                        </label>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="mt-4 text-xs text-gray-600 italic">
                                💡 Si tu coches majoritairement dans un niveau, c'est probablement ton niveau actuel. 
                                Pour progresser, vise les exemples du niveau supérieur !
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Modal de Détails des Sous-Compétences */}
          <AnimatePresence>
            {selectedCategory && selectedSubCompetence && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] flex items-center justify-center p-4"
                onClick={() => {
                  setSelectedSubCompetence(null);
                  setSubActiveTab('contenu');
                }}
              >
                <div className="absolute inset-0 bg-black/70" />
                {(() => {
                  const sub = selectedCategory.subCompetences.find(s => s.id === selectedSubCompetence);
                  if (!sub) return null;
                  
                  return (
                    <motion.div
                      initial={{ scale: 0.9, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.9, y: 20 }}
                      onClick={(e) => e.stopPropagation()}
                      className="relative bg-white rounded-xl shadow-2xl max-w-[90vw] w-full max-h-[95vh] overflow-hidden flex flex-col"
                    >
                      <div
                        className="p-6 text-white flex-shrink-0"
                        style={{ backgroundColor: selectedCategory.color }}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="text-sm text-white/80 mb-1">{selectedCategory.name}</div>
                            <h3 className="text-2xl font-bold">{sub.name}</h3>
                            <p className="text-white/90 mt-2">{sub.description}</p>
                          </div>
                          <button
                            onClick={() => setSelectedSubCompetence(null)}
                            className="text-white hover:bg-white/10 rounded-full p-2"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {/* Score */}
                        <div className="flex items-center gap-4 mt-4">
                          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                            <div className="text-xs text-white/80">Score actuel</div>
                            <div className="text-2xl font-bold">{sub.score}%</div>
                          </div>
                          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                            <div className="text-xs text-white/80">Minimum requis</div>
                            <div className="text-2xl font-bold">{sub.minRequired}%</div>
                          </div>
                          {sub.score >= sub.minRequired ? (
                            <div className="ml-auto bg-green-500/20 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-300" />
                              <span className="font-semibold text-green-100">Objectif atteint</span>
                            </div>
                          ) : (
                            <div className="ml-auto bg-red-500/20 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5 text-red-300" />
                              <span className="font-semibold text-red-100">
                                {sub.minRequired - sub.score}% à gagner
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Onglets pour la sous-compétence */}
                      <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0">
                        <div className="flex overflow-x-auto">
                          {[
                            { id: 'contenu', label: 'Contenu Complet', icon: BookOpen },
                            { id: 'matrice', label: 'Matrice de Progression', icon: BarChart3 },
                          ].map((tab) => {
                            const Icon = tab.icon;
                            return (
                              <button
                                key={tab.id}
                                onClick={() => setSubActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-all whitespace-nowrap ${
                                  subActiveTab === tab.id
                                    ? 'border-b-2 text-white'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                                style={
                                  subActiveTab === tab.id
                                    ? {
                                        borderBottomColor: selectedCategory.color,
                                        backgroundColor: `${selectedCategory.color}15`,
                                      }
                                    : {}
                                }
                              >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="p-8 flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
                        {/* Contenu Complet - Fusion de tous les onglets */}
                        {subActiveTab === 'contenu' && (
                          <div className="space-y-8">
                            {/* Description */}
                            <div className="bg-gradient-to-br from-white via-blue-50/30 to-white p-6 rounded-xl border-2 border-blue-100 shadow-sm">
                              <p className="text-gray-700 leading-relaxed text-lg">{sub.description}</p>
                            </div>

                            {/* Esprit */}
                            {sub.esprit && (
                              <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 p-8 rounded-2xl border-2 border-yellow-300 shadow-lg">
                                <div className="flex items-center gap-4 mb-6">
                                  <div className="p-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg">
                                    <Lightbulb className="w-8 h-8 text-white" />
                                  </div>
                                  <div>
                                    <h4 className="text-2xl font-bold text-gray-800">L'Esprit</h4>
                                    <p className="text-sm text-gray-600 mt-1">La mentalité et l'approche à adopter</p>
                                  </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-yellow-200">
                                  <p className="text-gray-800 leading-relaxed text-lg font-medium">{sub.esprit}</p>
                                </div>
                              </div>
                            )}

                            {/* Psychologie */}
                            {sub.psychologie && (
                              <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-fuchsia-50 p-8 rounded-2xl border-2 border-purple-300 shadow-lg">
                                <div className="flex items-center gap-4 mb-6">
                                  <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
                                    <Brain className="w-8 h-8 text-white" />
                                  </div>
                                  <div>
                                    <h4 className="text-2xl font-bold text-gray-800">La Psychologie</h4>
                                    <p className="text-sm text-gray-600 mt-1">Pièges à éviter et insights psychologiques</p>
                                  </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-purple-200">
                                  <div className="prose prose-lg max-w-none">
                                    <p className="text-gray-800 leading-relaxed whitespace-pre-line text-base font-medium">{sub.psychologie}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Pédagogie */}
                            {sub.explicationPedagogique && (
                              <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 rounded-2xl border-2 border-green-300 shadow-lg">
                                <div className="flex items-center gap-4 mb-6">
                                  <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                                    <BookOpen className="w-8 h-8 text-white" />
                                  </div>
                                  <div>
                                    <h4 className="text-2xl font-bold text-gray-800">Explication Pédagogique</h4>
                                    <p className="text-sm text-gray-600 mt-1">Comprendre en profondeur cette sous-compétence</p>
                                  </div>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-green-200">
                                  <div className="prose prose-lg max-w-none">
                                    <p className="text-gray-800 leading-relaxed text-base font-medium">{sub.explicationPedagogique}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Exemples concrets */}
                            {sub.exemplesConcrets && sub.exemplesConcrets.length > 0 && (
                              <div className="bg-gradient-to-br from-orange-50/50 via-white to-orange-50/30 p-8 rounded-2xl border-2 border-orange-100 shadow-sm">
                                <div className="flex items-center gap-3 mb-6">
                                  <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                                    <Zap className="w-6 h-6 text-white" />
                                  </div>
                                  <h4 className="text-2xl font-bold text-gray-800">Exemples concrets</h4>
                                </div>
                                <div className="space-y-3">
                                  {sub.exemplesConcrets.map((exemple, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-white p-5 rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-md transition-all"
                                    >
                                      <p className="text-gray-700 leading-relaxed">{exemple}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Matrice de Progression pour sous-compétence */}
                        {subActiveTab === 'matrice' && (
                          <div className="space-y-8">
                            <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50 p-8 rounded-2xl border-2 border-blue-300 shadow-lg">
                              <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-lg">
                                  <BarChart3 className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                  <h4 className="text-2xl font-bold text-gray-800">Matrice de Progression</h4>
                                  <p className="text-base text-gray-600 mt-1">Exemples concrets pour chaque niveau - Identifie où tu es et où tu veux aller</p>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-3 gap-6">
                                {directeurCompteLevels.map((level, idx) => {
                                  // Générer des exemples concrets selon la sous-compétence
                                  const getSubExamples = () => {
                                    // Plan Commercial Annuel
                                    if (sub.id === 'plan-commercial') {
                                      if (idx === 0) {
                                        return [
                                          'Plan Excel simple avec objectifs chiffrés (ex: 1.5M$ CA)',
                                          'Révision trimestrielle basique',
                                          'Suivi de 3-4 KPIs principaux (CA, contrats, pipeline)',
                                          'Plan documenté sur 1-2 pages'
                                        ];
                                      } else if (idx === 1) {
                                        return [
                                          'Plan stratégique documenté avec analyse SWOT',
                                          'Révision mensuelle avec ajustements tactiques',
                                          'KPIs avancés avec analyse de tendances',
                                          'Plan structuré avec jalons trimestriels clairs',
                                          'Alignement avec stratégie BZ et objectifs équipe'
                                        ];
                                      } else {
                                        return [
                                          'Plan visionnaire multi-scénarios (optimiste, réaliste, pessimiste)',
                                          'Révision continue avec innovations stratégiques',
                                          'KPIs prédictifs avec modèles de prévision',
                                          'Plan exhaustif avec roadmap 12-18 mois',
                                          'Mentorat d\'autres directeurs sur la planification',
                                          'Contribution à la stratégie commerciale BZ'
                                        ];
                                      }
                                    }
                                    // Gestion de Territoire
                                    if (sub.id === 'territoire') {
                                      if (idx === 0) return ['Mapping basique du territoire', 'Liste de 20-30 entreprises cibles', 'Approche générique par secteur'];
                                      if (idx === 1) return ['Mapping détaillé avec priorités', 'Liste de 50+ entreprises ICP qualifiées', 'Stratégie par secteur documentée', 'Optimisation géographique'];
                                      return ['Mapping stratégique avec ROI par zone', 'Base de 100+ prospects qualifiés', 'Stratégie sectorielle avec expertise', 'Expansion territoriale planifiée'];
                                    }
                                    // Forecast
                                    if (sub.id === 'forecast') {
                                      if (idx === 0) return ['Forecast mensuel avec précision ±15%', 'Pipeline basique dans CRM', 'Révision hebdomadaire'];
                                      if (idx === 1) return ['Forecast précis à ±5%', 'Pipeline détaillé avec probabilités', 'Révision bi-hebdomadaire avec ajustements'];
                                      return ['Forecast précis à ±2%', 'Pipeline prédictif avec modèles', 'Révision continue', 'Mentorat sur forecast'];
                                    }
                                    // Stratégie par Secteur
                                    if (sub.id === 'strategie-secteur') {
                                      if (idx === 0) return ['Approche générique par secteur', 'Connaissance basique des enjeux', 'Pitch standardisé'];
                                      if (idx === 1) return ['Stratégie documentée par secteur', 'Expertise sectorielle démontrée', 'Pitch adapté par industrie'];
                                      return ['Stratégie sectorielle avec ROI', 'Expertise reconnue dans 2-3 secteurs', 'Innovation par secteur', 'Thought leadership'];
                                    }
                                    // Objectifs & Quotas
                                    if (sub.id === 'objectifs-quotas') {
                                      if (idx === 0) return ['Suivi basique des quotas mensuels', 'Objectifs individuels clairs', 'Reporting mensuel'];
                                      if (idx === 1) return ['Gestion proactive des quotas', 'Objectifs équipe alignés', 'Reporting avancé avec analyse'];
                                      return ['Optimisation continue des quotas', 'Leadership sur objectifs équipe', 'Stratégie de dépassement', 'Mentorat sur performance'];
                                    }
                                    // Exemples génériques pour autres sous-compétences
                                    return [
                                      `Niveau ${idx + 1}: Maîtrise basique - Exemple concret 1`,
                                      `Niveau ${idx + 1}: Maîtrise basique - Exemple concret 2`,
                                      `Niveau ${idx + 1}: Maîtrise basique - Exemple concret 3`
                                    ];
                                  };

                                  const examples = getSubExamples();
                                  const minScoreForLevel = idx === 0 ? 60 : idx === 1 ? 75 : 90;
                                  const isAtLevel = sub.score >= minScoreForLevel && 
                                    (idx === directeurCompteLevels.length - 1 || sub.score < (idx === 0 ? 75 : 90));

                                return (
                                  <motion.div
                                    key={level.level}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className={`bg-white rounded-xl p-6 border-2 shadow-md transition-all hover:shadow-xl ${
                                      isAtLevel 
                                        ? 'border-orange-400 ring-4 ring-orange-200 scale-105' 
                                        : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 mb-5">
                                      <div
                                        className="w-6 h-6 rounded-full shadow-md"
                                        style={{ backgroundColor: level.color }}
                                      />
                                      <div className="flex-1">
                                        <h5 className="font-bold text-gray-800 text-lg">{level.level}</h5>
                                        <p className="text-xs text-gray-500 font-medium">{minScoreForLevel}%+ requis</p>
                                      </div>
                                      {isAtLevel && (
                                        <span className="px-3 py-1 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-md animate-pulse whitespace-nowrap">
                                          TON NIVEAU
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                        <Target className="w-4 h-4" style={{ color: level.color }} />
                                        Exemples concrets à atteindre :
                                      </p>
                                      {examples.map((example, exIdx) => (
                                        <div
                                          key={exIdx}
                                          className="flex items-start gap-3 text-sm text-gray-700 bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                                        >
                                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                          <span className="leading-relaxed">{example}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </motion.div>
                                );
                                })}
                              </div>
                            </div>

                            {/* Guide d'auto-évaluation pour sous-compétence */}
                            <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-xl p-6 border-2 border-blue-300 shadow-md">
                              <h5 className="font-bold text-gray-800 mb-4 flex items-center gap-3 text-lg">
                                <div className="p-2 bg-blue-500 rounded-lg">
                                  <Target className="w-5 h-5 text-white" />
                                </div>
                                Comment t'auto-évaluer sur cette sous-compétence
                              </h5>
                              <div className="space-y-3 text-sm text-gray-700">
                                <p className="font-semibold">✅ Coche les exemples que tu maîtrises actuellement :</p>
                                <div className="grid md:grid-cols-3 gap-4">
                                  {directeurCompteLevels.map((level, levelIdx) => {
                                    // Fonction pour obtenir les exemples selon la sous-compétence et le niveau
                                    const getExamplesForLevel = (subId: string, idx: number): string[] => {
                                      if (subId === 'plan-commercial') {
                                        if (idx === 0) return ['Plan Excel simple avec objectifs chiffrés', 'Révision trimestrielle basique', 'Suivi de 3-4 KPIs principaux'];
                                        if (idx === 1) return ['Plan stratégique documenté avec analyse SWOT', 'Révision mensuelle avec ajustements', 'KPIs avancés avec analyse'];
                                        return ['Plan visionnaire multi-scénarios', 'Révision continue avec innovations', 'KPIs prédictifs avec modèles'];
                                      }
                                      if (subId === 'territoire') {
                                        if (idx === 0) return ['Mapping basique du territoire', 'Liste de 20-30 entreprises cibles', 'Approche générique par secteur'];
                                        if (idx === 1) return ['Mapping détaillé avec priorités', 'Liste de 50+ entreprises ICP', 'Stratégie par secteur documentée'];
                                        return ['Mapping stratégique avec ROI', 'Base de 100+ prospects qualifiés', 'Stratégie sectorielle avec expertise'];
                                      }
                                      if (subId === 'forecast') {
                                        if (idx === 0) return ['Forecast mensuel ±15%', 'Pipeline basique dans CRM', 'Révision hebdomadaire'];
                                        if (idx === 1) return ['Forecast précis à ±5%', 'Pipeline détaillé avec probabilités', 'Révision bi-hebdomadaire'];
                                        return ['Forecast précis à ±2%', 'Pipeline prédictif avec modèles', 'Révision continue'];
                                      }
                                      return [`Exemple niveau ${idx + 1} - 1`, `Exemple niveau ${idx + 1} - 2`, `Exemple niveau ${idx + 1} - 3`];
                                    };

                                    const levelExamples = getExamplesForLevel(sub.id, levelIdx).slice(0, 3);
                                    
                                    return (
                                      <div key={level.level} className="space-y-2">
                                        <p className="font-medium text-xs text-gray-600 mb-2 flex items-center gap-2">
                                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: level.color }} />
                                          {level.level}:
                                        </p>
                                        {levelExamples.map((ex: string, i: number) => (
                                          <label key={i} className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                                            <input type="checkbox" className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            <span className="leading-relaxed">{ex}</span>
                                          </label>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs text-gray-700 leading-relaxed">
                                    💡 <strong>Astuce :</strong> Si tu coches majoritairement dans un niveau, c'est probablement ton niveau actuel. 
                                    Pour progresser, vise les exemples du niveau supérieur ! Chaque exemple coched = +5-10% sur ta compétence.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      </div>
                    </motion.div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

